import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    MenuItem,
    Button,
    Typography
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";

import useMicroSlideApi from "../../../api/MicroSlideApi";
import ConfirmDialog from "../../Common/ConfirmDialog";

// MicroSlide assay model types
const ASSAY_MODELS = [
    { value: "0", label: "CM (Colorimetric)" },
    { value: "1", label: "CM Blank" },
    { value: "2", label: "PM (Potentiometric)" },
    { value: "3", label: "PM Blank" },
    { value: "4", label: "Two Point Rate" },
    { value: "5", label: "N Point Rate" },
    { value: "6", label: "DM (Dual Wavelength)" },
    { value: "7", label: "DM Blank" },
    { value: "8", label: "IR" },
    { value: "9", label: "REF" }
];

export default function Header({
    mode = "response",
    rows,
    parameters,
    setParameters,
    extras,
    setResultDataMap,
    onRun,
    onResult,
    title
}) {
    const [model, setModel] = useState(
        String(parameters?.eAssayModelType ?? "6")
    );

    const [description, setDescription] = useState("");

    // Validation dialog
    const [validationOpen, setValidationOpen] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");

    useEffect(() => {
        if (parameters?.eAssayModelType !== undefined) {
            setModel(String(parameters.eAssayModelType));
        }
    }, [parameters?.eAssayModelType]);

    const handleModelChange = (e) => {
        const v = e.target.value;

        setModel(v);

        setParameters?.(prev => ({
            ...prev,
            eAssayModelType: v
        }));
    };

    const {
        callResponse,
        callCalibrate,
        callPredict,
        loading,
        error
    } = useMicroSlideApi(
        parameters,
        rows,
        extras
    );

    const buttonLabel = {
        response: "Calculate Response",
        calibrate: "Calibrate",
        predict: "Predict"
    }[mode] || "Run";

    // ============================================================
    // VALIDATION
    // ============================================================

    const showValidationError = (message) => {
        setValidationMessage(message);
        setValidationOpen(true);
    };

    const validateCalibration = () => {
        // --------------------------------------------------------
        // 1. Calibration rows must exist
        // --------------------------------------------------------

        if (!Array.isArray(rows) || rows.length === 0) {
            return "Calibration data is empty. Please enter calibration data before calibrating.";
        }

        // --------------------------------------------------------
        // 2. At least one usable calibration row
        // --------------------------------------------------------

        const validRows = rows.filter(row => {
            if (!row) return false;

            const concentration =
                row?.concentration ??
                row?.Concentration ??
                row?.sav ??
                row?.SAV;

            const response1 =
                row?.resp1 ??
                row?.Response1 ??
                row?.response1;

            const response2 =
                row?.resp2 ??
                row?.Response2 ??
                row?.response2;

            return (
                concentration !== undefined &&
                concentration !== null &&
                String(concentration).trim() !== "" &&
                (
                    response1 !== undefined ||
                    response2 !== undefined
                ) &&
                (
                    String(response1 ?? "").trim() !== "" ||
                    String(response2 ?? "").trim() !== ""
                )
            );
        });

        if (validRows.length === 0) {
            return "Calibration data is empty. Please enter concentration and response values before calibrating.";
        }

        // --------------------------------------------------------
        // 3. Validate every entered row
        // --------------------------------------------------------

        for (let index = 0; index < rows.length; index++) {
            const row = rows[index];

            if (!row) {
                return `Calibration row ${index + 1} is empty.`;
            }

            const concentration =
                row?.concentration ??
                row?.Concentration ??
                row?.sav ??
                row?.SAV;

            const response1 =
                row?.resp1 ??
                row?.Response1 ??
                row?.response1;

            const response2 =
                row?.resp2 ??
                row?.Response2 ??
                row?.response2;

            const hasConcentration =
                concentration !== undefined &&
                concentration !== null &&
                String(concentration).trim() !== "";

            const hasResponse1 =
                response1 !== undefined &&
                response1 !== null &&
                String(response1).trim() !== "";

            const hasResponse2 =
                response2 !== undefined &&
                response2 !== null &&
                String(response2).trim() !== "";

            if (!hasConcentration) {
                return `Concentration is missing in calibration row ${index + 1}.`;
            }

            if (!hasResponse1 && !hasResponse2) {
                return `Response value is missing in calibration row ${index + 1}.`;
            }
        }

        // --------------------------------------------------------
        // 4. Validate spline data
        // --------------------------------------------------------

        // Supports different possible locations used by the layout.
        const splineData =
            extras?.splines ??
            extras?.spline ??
            parameters?.splines ??
            parameters?.spline ??
            null;

        /*
         * If your calibration always requires spline data, this
         * validation applies to all calibration models.
         *
         * If spline is required only for K-Model, keep the
         * model === "4" condition.
         */
        if (model === "4") {
            const hasSpline =
                Array.isArray(splineData)
                    ? splineData.length > 0
                    : splineData &&
                        typeof splineData === "object"
                        ? Object.keys(splineData).length > 0
                        : typeof splineData === "string"
                            ? splineData.trim() !== ""
                            : false;

            if (!hasSpline) {
                return "Spline data is empty. Please provide valid spline data before calibrating.";
            }
        }

        return null;
    };

    // ============================================================
    // RESULT TRANSFORMATION
    // ============================================================

    const transformResult = (data) => {
        if (!data) return null;

        if (mode === "response") {
            return {
                Response1: data.dResponse1 ?? "-",
                Response2: data.dResponse2 ?? "-",
                FirstDr: data.dFirstDr ?? "-",
                SubDep: data.dSubDepCheckValue ?? "-",
                ConditionCode: data.zConditionCode ?? "",
                Error: data.zError ?? ""
            };
        }

        if (mode === "calibrate") {
            return {
                A0: data.fCalInterceptA0 ?? "-",
                A1: data.fCalSlopeA1 ?? "-",
                A2: data.fCalCurvatureA2 ?? "-",
                A3: data.fCalBlankCorrectionFactorA3 ?? "-",
                Calibrated: data.bABFCalibrated ?? "-",
                ConditionCode: data.zConditionCode ?? "",
                Error: data.zError ?? ""
            };
        }

        const preds = data.predictions || [];

        const summary = {
            "Sample Count": preds.length,
            predictions: preds
        };

        if (preds.length > 0) {
            summary["First Concentration"] =
                preds[0].concentration;

            summary["Last Concentration"] =
                preds[preds.length - 1].concentration;
        }

        return summary;
    };

    // ============================================================
    // SUBMIT
    // ============================================================

    const handleSubmit = async () => {

        // --------------------------------------------------------
        // CALIBRATION VALIDATION
        // --------------------------------------------------------

        if (mode === "calibrate") {
            const validationError = validateCalibration();

            if (validationError) {
                showValidationError(validationError);
                return;
            }
        } else {

            // Existing validation for response/predict
            if (!rows || rows.length === 0) {
                showValidationError(
                    "No input data available. Please enter data before running."
                );
                return;
            }
        }

        if (onRun) {
            onRun();
        }

        try {
            let data;

            if (mode === "response") {
                data = await callResponse();
            } else if (mode === "calibrate") {
                data = await callCalibrate();
            } else {
                data = await callPredict();
            }

            const formatted = transformResult(data);

            if (!formatted) {
                showValidationError(
                    "The API did not return a valid result."
                );
                return;
            }

            setResultDataMap?.(prev => {
                const prevData = prev || {};

                const nextRun =
                    Object.keys(prevData).length + 1;

                const finalDescription =
                    description &&
                        description.trim() !== ""
                        ? description
                        : `Run ${nextRun}`;

                const newEntry = {
                    run: nextRun,
                    description: finalDescription,
                    result: formatted,
                    rows,
                    parameters
                };

                const updated = {
                    ...prevData,
                    [nextRun]: newEntry
                };

                sessionStorage.setItem(
                    `microslideHistory_${mode}`,
                    JSON.stringify(updated)
                );

                return updated;
            });

            if (onResult) {
                onResult(data, formatted);
            }

        } catch (err) {
            console.error(
                "MicroSlide API ERROR:",
                err
            );

            showValidationError(
                err?.message ||
                "An error occurred while processing the request."
            );
        }
    };

    return (
        <>
            <Box
                component="form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                }}
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-start",
                    backgroundColor: "#fff"
                }}
            >
                <Box
                    sx={{
                        width: "80%",
                        display: "flex",
                        gap: 2,
                        p: 2,
                        borderRadius: 3,
                        boxShadow:
                            "0 4px 20px rgba(0,0,0,0.08)",
                        alignItems: "stretch"
                    }}
                >

                    {/* Math Model */}
                    <TextField
                        select
                        label="Math Model"
                        value="4"
                        sx={{
                            flex: 1,
                            minWidth: 140
                        }}
                        size="medium"
                    >
                        <MenuItem value="0">
                            Linear
                        </MenuItem>

                        <MenuItem value="1">
                            Logit4
                        </MenuItem>

                        <MenuItem value="2">
                            Logit5
                        </MenuItem>

                        <MenuItem value="3">
                            Cubic Spline
                        </MenuItem>

                        <MenuItem value="4">
                            K-Model
                        </MenuItem>
                    </TextField>

                    {/* Assay Model */}
                    <TextField
                        select
                        label="Assay Model"
                        value={model}
                        onChange={handleModelChange}
                        sx={{ flex: 1 }}
                        size="medium"
                    >
                        {ASSAY_MODELS.map(m => (
                            <MenuItem
                                key={m.value}
                                value={m.value}
                            >
                                {m.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* Description */}
                    <TextField
                        label={
                            title ||
                            "Description"
                        }
                        value={description}
                        onChange={(e) =>
                            setDescription(
                                e.target.value
                            )
                        }
                        sx={{ flex: 3 }}
                        size="medium"
                    />

                    {/* Submit */}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        startIcon={<ScienceIcon />}
                        disabled={loading}
                    >
                        {loading
                            ? `${buttonLabel}...`
                            : buttonLabel}
                    </Button>

                    {error && (
                        <Typography color="error">
                            {error}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ width: "20%" }} />
            </Box>

            {/* ================================================== */}
            {/* VALIDATION DIALOG */}
            {/* ================================================== */}

            <ConfirmDialog
                open={validationOpen}
                title="Validation Error"
                message={validationMessage}
                onConfirm={() =>
                    setValidationOpen(false)
                }
                onCancel={() =>
                    setValidationOpen(false)
                }
            />
        </>
    );
}

